/**
 * Exact formatting for token amounts that arrive as integer strings in a
 * currency's smallest unit.
 *
 * Why this exists next to `features/wallet/utils.ts`, which also converts raw
 * balances: that one infers decimals from the symbol (`usdt`/`usdc` → 6,
 * everything else → 18) and returns a `number`. Both are wrong for this data.
 * The referral API states `decimals` per record, and its amounts routinely
 * exceed Number.MAX_SAFE_INTEGER (2^53-1 ≈ 9.007e15) — an 18-decimal token
 * passes that at only 0.009 of a whole unit, so `Number(raw)` silently drops
 * digits on essentially every real value:
 *
 *   Number("1234567890123456789")        → 1234567890123456800   ← wrong
 *   BigInt("1234567890123456789")        → exact
 *
 * Everything here stays in BigInt and strings. No value ever passes through a
 * float, including the grouping step: BigInt.prototype.toLocaleString is
 * arbitrary-precision, unlike Number.prototype.toLocaleString.
 *
 * The wallet helper is deliberately left alone — it is used by the navbar and
 * wallet page and changing its contract is out of scope here.
 */

export type TokenAmountOptions = {
  locale?: "fa" | "en";
  /** Digits kept after the separator. Default 6; trailing zeros are trimmed. */
  maxFractionDigits?: number;
  /** Render exact zero as "0" rather than "0.000000". Default true. */
  trimZeros?: boolean;
};

const DIGITS_CACHE = new Map<string, string[]>();
const SEPARATOR_CACHE = new Map<string, string>();

function intlLocale(locale: "fa" | "en"): string {
  return locale === "fa" ? "fa-IR" : "en-US";
}

/**
 * The locale's own digit glyphs. Needed because the integer part is grouped by
 * Intl (Persian digits in fa) while the fraction is assembled from ASCII — a
 * mixed-script number like "۱۲۳.456" is what you get if you skip this.
 */
function localeDigits(locale: "fa" | "en"): string[] {
  const key = intlLocale(locale);
  const cached = DIGITS_CACHE.get(key);
  if (cached) return cached;
  const formatter = new Intl.NumberFormat(key, { useGrouping: false });
  const digits = Array.from({ length: 10 }, (_, d) => formatter.format(d));
  DIGITS_CACHE.set(key, digits);
  return digits;
}

function decimalSeparator(locale: "fa" | "en"): string {
  const key = intlLocale(locale);
  const cached = SEPARATOR_CACHE.get(key);
  if (cached) return cached;
  const parts = new Intl.NumberFormat(key).formatToParts(1.1);
  const separator = parts.find((p) => p.type === "decimal")?.value ?? ".";
  SEPARATOR_CACHE.set(key, separator);
  return separator;
}

/**
 * Parse a raw smallest-unit amount. Returns null for anything that is not a
 * clean integer string, so callers render a dash instead of a wrong number.
 *
 * Accepts an optional leading `-` and tolerates a `.0`-style trailing fraction
 * (some serializers emit `"1000.0"` for an integer column) but refuses any
 * value with real fractional digits — silently truncating those would be the
 * same class of bug this module exists to prevent.
 */
export function parseSmallestUnit(raw: string | number | null | undefined): bigint | null {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).trim();
  if (!text) return null;

  const match = /^(-?\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) return null;
  if (match[2] && /[1-9]/.test(match[2])) return null;

  try {
    return BigInt(match[1]);
  } catch {
    return null;
  }
}

/** Split |value| into integer and zero-padded fraction digits. */
function splitUnits(value: bigint, decimals: number): { negative: boolean; whole: bigint; fraction: string } {
  const negative = value < BigInt(0);
  const absolute = negative ? -value : value;
  if (decimals <= 0) return { negative, whole: absolute, fraction: "" };
  const base = BigInt(10) ** BigInt(decimals);
  return {
    negative,
    whole: absolute / base,
    fraction: (absolute % base).toString().padStart(decimals, "0"),
  };
}

/**
 * Smallest-unit amount → display string. Never throws; returns `fallback` when
 * the input cannot be trusted.
 */
export function formatTokenAmount(
  raw: string | number | null | undefined,
  decimals: number | null | undefined,
  options: TokenAmountOptions = {},
  fallback = "—",
): string {
  const value = parseSmallestUnit(raw);
  if (value === null) return fallback;

  // A missing `decimals` is a contract violation, not a zero-decimal token.
  // Guessing 18 here would misprice a 6-decimal balance by 10^12.
  if (decimals === null || decimals === undefined || !Number.isInteger(decimals) || decimals < 0 || decimals > 36) {
    return fallback;
  }

  const { locale = "en", maxFractionDigits = 6, trimZeros = true } = options;
  const { negative, whole, fraction } = splitUnits(value, decimals);

  const digits = localeDigits(locale);
  const groupedWhole = whole.toLocaleString(intlLocale(locale));

  let fractionOut = fraction.slice(0, Math.max(0, maxFractionDigits));
  if (trimZeros) fractionOut = fractionOut.replace(/0+$/, "");

  const localizedFraction = fractionOut.replace(/\d/g, (d) => digits[Number(d)]);
  const sign = negative ? "-" : "";

  // A non-zero amount too small to show at this precision must not render as
  // "0" — on an earnings screen that reads as "you earned nothing" when the
  // ledger says otherwise. Show the threshold instead.
  if (value !== BigInt(0) && whole === BigInt(0) && !localizedFraction) {
    const smallest = maxFractionDigits > 0
      ? `0${decimalSeparator(locale)}${"0".repeat(maxFractionDigits - 1)}1`.replace(
          /\d/g,
          (d) => digits[Number(d)],
        )
      : (1).toLocaleString(intlLocale(locale));
    return `${sign}<${smallest}`;
  }

  const body = localizedFraction
    ? `${groupedWhole}${decimalSeparator(locale)}${localizedFraction}`
    : groupedWhole;

  return `${sign}${body}`;
}

/**
 * Trim a fixed-scale decimal string for display: "25.0000000000" → "25",
 * "5.5000000000" → "5.5". Done on the string rather than via parseFloat so the
 * value is never rounded, whatever scale the backend sends.
 */
export function formatDecimalString(
  raw: string | number | null | undefined,
  options: { locale?: "fa" | "en"; maxFractionDigits?: number } = {},
  fallback = "—",
): string {
  if (raw === null || raw === undefined) return fallback;
  const text = String(raw).trim();
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(text);
  if (!match) return fallback;

  const { locale = "en", maxFractionDigits = 4 } = options;
  const [, sign, whole, fractionRaw = ""] = match;

  const fraction = fractionRaw.slice(0, Math.max(0, maxFractionDigits)).replace(/0+$/, "");
  const digits = localeDigits(locale);
  const groupedWhole = BigInt(whole).toLocaleString(intlLocale(locale));
  const localizedFraction = fraction.replace(/\d/g, (d) => digits[Number(d)]);

  return `${sign}${localizedFraction ? `${groupedWhole}${decimalSeparator(locale)}${localizedFraction}` : groupedWhole}`;
}
